// Per-role NavSection configs. Extracted so every page imports the
// same nav (the alternative is duplicating it across 20 pages and
// fighting drift forever).

import {
  LayoutDashboard, Package, Users, CreditCard, History, Repeat,
  Settings, Inbox, Banknote, Store, Receipt, AlertTriangle, TrendingUp,
  FileText, ShoppingBag, BookOpen, Gift, Tag, Percent, Calculator, HelpCircle, SlidersHorizontal,
  Smartphone } from 'lucide-react';
import type { NavSection } from '../components/shared/PortalShell';
import { airtimeEnabled } from '../lib/featureFlags';

export const CUSTOMER_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',     icon: LayoutDashboard, href: '/customer' },
    { id: 'requests',   label: 'Requests',      icon: Package,         href: '/customer/requests' },
    // Airtime sits third, between Requests and Care packages, per the Web
    // Portal v2 design. It is flag-gated: the route and the dashboard tiles
    // are already gated the same way, and without this entry a customer with
    // the flag on could reach the page but had no sidebar link to it.
    //
    // Spread inline rather than pushed later so the nav stays one literal —
    // five pages previously kept their own copy to insert this, and every one
    // of them drifted in a different direction.
    ...(airtimeEnabled()
      ? [{ id: 'airtime', label: 'Airtime', icon: Smartphone, href: '/customer/airtime' }]
      : []),
    { id: 'packages',   label: 'Care packages', icon: Gift,            href: '/customer/packages' },
    { id: 'planner',    label: 'Basket planner', icon: Calculator,     href: '/customer/plan' },
    { id: 'recipients', label: 'Recipients',    icon: Users,           href: '/customer/recipients' },
  ]},
  { title: 'Payments', items: [
    { id: 'methods',    label: 'Payment methods', icon: CreditCard, href: '/customer/payment-methods' },
    { id: 'history',    label: 'History',         icon: History,    href: '/customer/history' },
    { id: 'recurring',  label: 'Recurring',       icon: Repeat,     href: '/customer/recurring' },
  ]},
  { title: 'Account', items: [
    { id: 'settings',   label: 'Settings',        icon: Settings,   href: '/customer/settings' },
    { id: 'help',       label: 'Help',            icon: HelpCircle, href: '/customer/help' },
  ]},
];

export const RECIPIENT_NAV: NavSection[] = [
  { items: [
    { id: 'home',     label: 'Home',    icon: LayoutDashboard, href: '/recipient' },
    { id: 'planner',  label: 'Basket planner', icon: Calculator, href: '/recipient/plan' },
    { id: 'history',  label: 'History', icon: History,         href: '/recipient/history' },
  ]},
  { title: 'Account', items: [
    { id: 'settings', label: 'Settings', icon: Settings, href: '/recipient/settings' },
    { id: 'help',     label: 'Help',     icon: HelpCircle, href: '/recipient/help' },
  ]},
];

export const MERCHANT_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',      icon: LayoutDashboard, href: '/merchant' },
    { id: 'inbox',      label: 'Quote requests', icon: Inbox,           href: '/merchant/quote-requests' },
    { id: 'orders',     label: 'Active orders',  icon: Package,         href: '/merchant/orders' },
    { id: 'inventory',  label: 'Inventory',      icon: ShoppingBag,     href: '/merchant/inventory' },
  ]},
  { title: 'Payouts', items: [
    { id: 'payouts',    label: 'Payouts',        icon: Banknote,        href: '/merchant/payouts' },
  ]},
  { title: 'Account', items: [
    { id: 'profile',    label: 'Business profile', icon: Store,         href: '/merchant/settings/profile' },
    { id: 'settings',   label: 'Settings',       icon: Settings,        href: '/merchant/settings' },
    { id: 'help',       label: 'Help',           icon: HelpCircle,      href: '/merchant/help' },
  ]},
];

export const ADMIN_NAV: NavSection[] = [
  { items: [
    { id: 'overview',  label: 'Overview',  icon: LayoutDashboard, href: '/admin' },
  ]},
  { title: 'Operations', items: [
    { id: 'users',     label: 'Users',     icon: Users,           href: '/admin/users' },
    { id: 'merchants', label: 'Merchants', icon: Store,           href: '/admin/merchants' },
    { id: 'packages',  label: 'Care packages', icon: Gift,        href: '/admin/care-packages' },
    { id: 'requests',  label: 'Requests',  icon: Receipt,         href: '/admin/requests' },
    { id: 'payouts',   label: 'Payouts',   icon: Banknote,        href: '/admin/payouts' },
    { id: 'disputes',  label: 'Disputes',  icon: AlertTriangle,   href: '/admin/disputes' },
  ]},
  { title: 'Platform', items: [
    { id: 'catalog',   label: 'Catalog',   icon: Tag,             href: '/admin/catalog' },
    { id: 'fx',        label: 'FX rates',  icon: TrendingUp,      href: '/admin/fx' },
    { id: 'fees',      label: 'Fees',      icon: Percent,         href: '/admin/fees' },
    { id: 'platform',  label: 'Platform config', icon: SlidersHorizontal, href: '/admin/platform' },
    { id: 'audit',     label: 'Audit log', icon: FileText,        href: '/admin/audit-logs' },
    { id: 'docs',      label: 'Docs',      icon: BookOpen,        href: '/admin/docs' },
    { id: 'help',      label: 'Help',      icon: HelpCircle,      href: '/admin/help' },
    { id: 'settings',  label: 'Settings',  icon: Settings,        href: '/admin/settings' },
  ]},
];
